import type { z } from "zod";
import type { CastAd } from "./ads/cast";
import { castAd } from "./ads/cast";
import type { LinkAd } from "./ads/link";
import { linkAd } from "./ads/link";
import type { MiniAppAd } from "./ads/miniapp";
import { miniappAd } from "./ads/miniapp";
import type { FarcasterProfileAd } from "./ads/profile";
import { farcasterProfileAd } from "./ads/profile";
import type { TokenAd } from "./ads/token";
import { tokenAd } from "./ads/token";
import type { TweetAd } from "./ads/tweet";
import { tweetAd } from "./ads/tweet";
import type { XProfileAd } from "./ads/x-profile";
import { xProfileAd } from "./ads/x-profile";

/**
 * Union type for all complete ad structures with type, data, and optional metadata
 */
export type AdData =
  | CastAd
  | LinkAd
  | MiniAppAd
  | TokenAd
  | TweetAd
  | FarcasterProfileAd
  | XProfileAd;

/**
 * Registry of all ad definitions
 * Each entry is an AdDefinition object
 */
export const ads = {
  link: linkAd,
  cast: castAd,
  tweet: tweetAd,
  miniapp: miniappAd,
  token: tokenAd,
  farcasterProfile: farcasterProfileAd,
  xProfile: xProfileAd,
} as const;

export const adTypes = [
  "link",
  "cast",
  "tweet",
  "miniapp",
  "token",
  "farcasterProfile",
  "xProfile",
] as const;

/**
 * Type for ad definition keys
 */
export type AdType = keyof typeof ads;

/**
 * Get ad definition by type
 */
export function getAd(type: AdType) {
  return ads[type];
}
/**
 * Validate ad data against any ad definition
 */
export function validateAdData(data: unknown): {
  success: boolean;
  data?: {
    type: AdType;
    data: unknown;
  };
  error?: z.ZodError | string;
} {
  const record =
    typeof data === "object" && data !== null
      ? (data as { type?: unknown; data?: unknown })
      : {};
  const dataType = record.type;
  if (
    typeof dataType !== "string" ||
    !(adTypes as readonly string[]).includes(dataType)
  ) {
    return {
      success: false,
      error: "Data does not match any known ad type",
    };
  }
  const ad = getAd(dataType as AdType);

  const result = ad.data.safeParse(record.data);
  if (!result.success) {
    return {
      success: false,
      error: result.error,
    };
  }

  return {
    success: true,
    data: {
      type: dataType as AdType,
      data: result.data,
    },
  };
}

// Export all ad definitions
export * from "./ads";
// Export CAIP-10 helpers and the token chain registry
export * from "./caip10";
// Export constants
export { adlandApiUrl } from "./constants";
// Export core utilities
export * from "./core/ad-definition";

// Export Farcaster utilities
export * from "./farcaster";

// The API response shapes, and `castImage` — the single answer to "which
// picture does this cast carry", shared by the publish path and the renderer so
// the two cannot disagree.
export * from "./presentation";
// The publisher payout manifest. Shared because the API verifies the signature
// and the app asks a wallet to produce it — two codebases building one string.
export * from "./publisher-manifest";
export * from "./types";

// Backward compatibility aliases
export const adDefinitions = ads;
export const adModels = ads;
